import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button.jsx';
import { getVisibleInvitationCategories } from '../lib/invitationCatalog.js';
import { FaHeart, FaBirthdayCake, FaCross, FaBuilding, FaGlobe, FaDollarSign, FaPalette, FaBolt, FaSync, FaMobileAlt, FaHandshake } from 'react-icons/fa';
import '../styles/About.css';

const About = () => {
  const hiwSteps = [
    { number: 1, icon: '📋', title: 'Book Online', desc: 'Fill out the booking form with your event details and preferred design.' },
    { number: 2, icon: '💬', title: 'We Reach Out', desc: 'Our team contacts you via Messenger within 24 hours to confirm your order.' },
    { number: 3, icon: '💳', title: '50% Downpayment', desc: 'We send payment details through Messenger. Pay 50% to get started.' },
    { number: 4, icon: '🎨', title: 'Design Creation', desc: 'We craft your personalized e-invitation based on your preferences.' },
    { number: 5, icon: '✅', title: 'Review & Approve', desc: 'Check the design. Request revisions if needed, then give your approval.' },
    { number: 6, icon: '💰', title: 'Full Payment', desc: 'Once approved, settle the remaining 50% balance to receive your final file.' },
    { number: 7, icon: '⭐', title: 'Share & Feedback', desc: 'Share your invitation with guests and leave us feedback via Messenger or tag us in your post!' },
  ];

  const hiwCardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('hiw-card--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    hiwCardsRef.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: <FaHeart />,
      title: 'Wedding Invitations',
      description: 'Elegant digital invitations for your special day',
      path: '/wedding',
    },
    {
      icon: <FaBirthdayCake />,
      title: 'Birthday Invitations',
      description: 'Fun and creative designs for all ages',
      path: '/birthday',
    },
    {
      icon: <FaCross />,
      title: 'Christening Invitations',
      description: 'Beautiful invitations for baptism and dedication',
      path: '/christening',
    },
    {
      icon: <FaBuilding />,
      title: 'Corporate Events',
      description: 'Professional designs for business occasions',
      path: '/corporate',
    }
  ];
  const categoryLinks = getVisibleInvitationCategories();

  return (
    <div className="about">
      <section className="about-hero">
        <p className="about-badge">A Service by Logica Technology</p>
        <h1>About E-Invitation</h1>
        <p className="about-subtitle">
          Beautiful digital invitations for every Filipino celebration
        </p>
      </section>

      <section className="about-content">
        <div className="about-section">
          <h2>Who We Are</h2>
          <p>
            E-Invitation is a digital service proudly offered by <strong>Logica Technology</strong>, 
            a company dedicated to providing innovative digital solutions for Filipinos. 
            We specialize in creating stunning, personalized e-invitations that make your 
            special occasions even more memorable.
          </p>
          <p>
            Based in the Philippines, we understand the importance of celebrations in Filipino 
            culture. Whether it's a wedding, birthday, christening, or corporate event, we're 
            here to help you invite your loved ones in style!
          </p>
          <div className="website-link">
            <p>Visit our main website:</p>
            <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer" className="main-site-link">
              <FaGlobe /> logicatechnology.com
            </a>
          </div>
        </div>

        <div className="about-section">
          <h2>Our Services</h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-item">
                <span className="service-icon">{service.icon}</span>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to={service.path} className="service-link">
                  View category page
                </Link>
              </div>
            ))}
          </div>
        </div>

        <div className="about-section">
          <h2>Explore Digital Invitation Categories</h2>
          <p>
            We now have dedicated landing pages for the main event types so you can compare designs,
            pricing, turnaround, and booking direction before choosing a sample design or personalized concept.
          </p>
          <div className="about-link-grid">
            {categoryLinks.map((category) => (
              <Link key={category.value} to={category.path} className="about-link-card">
                <span>{category.label}</span>
                <strong>{category.headline}</strong>
                <p>{category.blurb}</p>
              </Link>
            ))}
          </div>
          <div className="about-inline-links">
            <Link to="/invitations">Browse all invitation designs</Link>
            <Link to="/digital-invitation-faq">Read the digital invitation FAQ</Link>
          </div>
        </div>

        <div className="about-section">
          <h2>Why Choose Us?</h2>
          <div className="values-grid">
            <div className="value-card">
              <h3><FaDollarSign /> Affordable</h3>
              <p>Quality digital invitations starting at ₱800</p>
            </div>
            <div className="value-card">
              <h3><FaPalette /> Custom Designs</h3>
              <p>Personalized invitations based on your theme and preferences</p>
            </div>
            <div className="value-card">
              <h3><FaBolt /> Fast Turnaround</h3>
              <p>Receive your e-invitation within 2-3 days</p>
            </div>
            <div className="value-card">
              <h3><FaSync /> Up to 10 Revisions</h3>
              <p>We'll keep refining until you're 100% satisfied</p>
            </div>
            <div className="value-card">
              <h3><FaMobileAlt /> Easy Sharing</h3>
              <p>Share via link, SMS, Messenger, or social media</p>
            </div>
            <div className="value-card">
              <h3>📋 RSVP Tracking</h3>
              <p>Get a live attendee summary email every time a guest RSVPs — see who's coming in real time</p>
            </div>
            <div className="value-card">
              <h3><FaHandshake /> Filipino Service</h3>
              <p>Local team that understands Filipino celebrations</p>
            </div>
          </div>
        </div>

        <section className="how-it-works">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Simple steps from booking to your beautiful e-invitation</p>
          </div>
          <div className="hiw-timeline">
            {hiwSteps.map((step, index) => (
              <div
                key={index}
                className="hiw-card"
                ref={el => { hiwCardsRef.current[index] = el; }}
                style={{ '--hiw-delay': `${index * 0.09}s` }}
              >
                <div className="hiw-track">
                  <div className="hiw-dot">{step.number}</div>
                  {index < hiwSteps.length - 1 && <div className="hiw-connector" />}
                </div>
                <div className="hiw-card-body">
                  <div className="hiw-icon-wrap">{step.icon}</div>
                  <div className="hiw-text">
                    <h4>{step.title}</h4>
                    <p>{step.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="about-section cta-section">
          <h2 className="about-cta-title">Ready to Get Started?</h2>
          <p>
            Let us create the perfect e-invitation for your special occasion!
          </p>
          <div className="cta-buttons">
            <Link to="/book">
              <Button variant="primary" size="large" className="about-cta-btn-primary">Book Now</Button>
            </Link>
            <Link to="/invitations">
              <Button variant="outline" size="large" className="about-cta-btn-outline">View Samples</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
